import { c as _c } from "react/compiler-runtime";
import './checkbox-group-buttons.scss';
import React, { PropsWithChildren, useMemo } from 'react';
import { AriaCheckboxGroupItemProps, AriaCheckboxGroupProps, CheckboxAria, useCheckboxGroup, useCheckboxGroupItem } from 'react-aria';
import { CheckboxGroupState, useCheckboxGroupState } from 'react-stately';
import { cn } from '../../utils/helpers.ts';
import { ButtonGroupInputType, ButtonGroupItem, ButtonGroupItemHookResult } from '../button-group-item/button-group-item.tsx';
export const CheckboxContext = React.createContext<CheckboxGroupState | null>(null);
type CheckboxGroupButtonsProps<TValue extends string = string> = PropsWithChildren & {
  errorMessage?: string;
  label?: string;
  hideLabel?: boolean;
  defaultValue?: TValue[];
  value?: TValue[];
  onChange?: (selectedValues: TValue[]) => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  validationState?: 'valid' | 'invalid';
  className?: string;
  listClassName?: string;
  selectionMode?: 'single' | 'multiple';
};
export const CheckboxGroupButtons = props => {
  const $ = _c(61);
  let children;
  let className;
  let defaultValue;
  let domProps;
  let errorMessage;
  let isDisabled;
  let isReadOnly;
  let isRequired;
  let label;
  let onChange;
  let t0;
  let t1;
  let t2;
  let validationState;
  let value;
  if ($[0] !== props) {
    ({
      children,
      errorMessage,
      label,
      hideLabel: t0,
      defaultValue,
      value,
      onChange,
      isDisabled,
      isReadOnly,
      isRequired,
      validationState,
      className,
      listClassName: t1,
      selectionMode: t2,
      ...domProps
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = defaultValue;
    $[4] = domProps;
    $[5] = errorMessage;
    $[6] = isDisabled;
    $[7] = isReadOnly;
    $[8] = isRequired;
    $[9] = label;
    $[10] = onChange;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = validationState;
    $[15] = value;
  } else {
    children = $[1];
    className = $[2];
    defaultValue = $[3];
    domProps = $[4];
    errorMessage = $[5];
    isDisabled = $[6];
    isReadOnly = $[7];
    isRequired = $[8];
    label = $[9];
    onChange = $[10];
    t0 = $[11];
    t1 = $[12];
    t2 = $[13];
    validationState = $[14];
    value = $[15];
  }
  const hideLabel = t0 === undefined ? false : t0;
  const listClassName = t1 === undefined ? "flex flex-wrap gap-comfy-sm" : t1;
  const selectionMode = t2 === undefined ? "multiple" : t2;
  let t3;
  if ($[16] !== onChange || $[17] !== selectionMode) {
    t3 = nextValues => {
      if (!onChange) {
        return;
      }
      if (selectionMode === "single") {
        const last = nextValues[nextValues.length - 1];
        onChange(last != null ? [last] : []);
      } else {
        onChange(nextValues);
      }
    };
    $[16] = onChange;
    $[17] = selectionMode;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== defaultValue || $[20] !== isDisabled || $[21] !== isReadOnly || $[22] !== isRequired || $[23] !== t3 || $[24] !== validationState || $[25] !== value) {
    t4 = {
      value,
      defaultValue,
      onChange: t3,
      isDisabled,
      isReadOnly,
      isRequired,
      validationState
    };
    $[19] = defaultValue;
    $[20] = isDisabled;
    $[21] = isReadOnly;
    $[22] = isRequired;
    $[23] = t3;
    $[24] = validationState;
    $[25] = value;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const baseState = useCheckboxGroupState(t4);
  let t5;
  if ($[27] !== baseState || $[28] !== selectionMode) {
    t5 = selectionMode === "single" ? {
      ...baseState,
      setValue: vals => {
        baseState.setValue(vals.length > 1 ? [vals[vals.length - 1]] : vals);
      }
    } : baseState;
    $[27] = baseState;
    $[28] = selectionMode;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  const state = t5;
  let t6;
  if ($[30] !== domProps || $[31] !== isDisabled || $[32] !== isReadOnly || $[33] !== isRequired || $[34] !== label || $[35] !== validationState) {
    t6 = {
      label,
      isDisabled,
      isReadOnly,
      isRequired,
      validationState,
      ...domProps
    };
    $[30] = domProps;
    $[31] = isDisabled;
    $[32] = isReadOnly;
    $[33] = isRequired;
    $[34] = label;
    $[35] = validationState;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  const ariaProps = t6;
  const {
    groupProps,
    labelProps,
    errorMessageProps
  } = useCheckboxGroup(ariaProps, state);
  let t7;
  if ($[37] !== className) {
    t7 = cn("CheckboxGroupButtons", className);
    $[37] = className;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== hideLabel || $[40] !== label || $[41] !== labelProps) {
    t8 = label && <p className={cn("CheckboxGroupButtons__label", {
      "sr-only": hideLabel
    })} {...labelProps}>{label}</p>;
    $[39] = hideLabel;
    $[40] = label;
    $[41] = labelProps;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== listClassName) {
    t9 = cn("CheckboxGroupButtons__list", listClassName);
    $[43] = listClassName;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== children || $[46] !== t9) {
    t10 = <div className={t9}>{children}</div>;
    $[45] = children;
    $[46] = t9;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== state || $[49] !== t10) {
    t11 = <CheckboxContext.Provider value={state}>{t10}</CheckboxContext.Provider>;
    $[48] = state;
    $[49] = t10;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== errorMessage || $[52] !== errorMessageProps || $[53] !== state) {
    t12 = errorMessage && state.isInvalid && <div {...errorMessageProps} style={{
      color: "var(--color-action-foreground-negative-potent)",
      fontSize: "var(--font-label-moderate-sm-font-size)"
    }}>{errorMessage}</div>;
    $[51] = errorMessage;
    $[52] = errorMessageProps;
    $[53] = state;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== groupProps || $[56] !== t11 || $[57] !== t12 || $[58] !== t7 || $[59] !== t8) {
    t13 = <div {...groupProps} className={t7}>{t8}{t11}{t12}</div>;
    $[55] = groupProps;
    $[56] = t11;
    $[57] = t12;
    $[58] = t7;
    $[59] = t8;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  return t13;
};
export type CheckboxGroupButtonsItemProps<TValue extends string = string> = PropsWithChildren<{
  value: TValue;
  id: string;
  itemContentClassName?: string;
  ariaLabel?: string;
}>;

/**
 * Adapter from react-aria's useCheckboxGroupItem to the shape ButtonGroupItem expects.
 * This is the correct hook to use with a CheckboxGroupState (NOT useCheckbox).
 */
export const useCheckboxGroupItemAdapter = (args, state, ref) => {
  const $ = _c(8);
  const t0 = args.ariaLabel || args.value;
  let t1;
  if ($[0] !== args.value || $[1] !== t0) {
    t1 = {
      value: args.value,
      "aria-label": t0
    };
    $[0] = args.value;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const itemProps = t1;
  const {
    inputProps,
    isSelected,
    isPressed,
    labelProps
  } = useCheckboxGroupItem(itemProps, state, ref);
  let t2;
  if ($[3] !== inputProps || $[4] !== isPressed || $[5] !== isSelected || $[6] !== labelProps) {
    t2 = {
      inputProps,
      isSelected,
      isPressed,
      labelProps
    };
    $[3] = inputProps;
    $[4] = isPressed;
    $[5] = isSelected;
    $[6] = labelProps;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const CheckboxGroupButtonsItem: React.FC<CheckboxGroupButtonsItemProps> = ({
  id,
  children,
  value,
  itemContentClassName,
  ariaLabel
}) => {
  return <ButtonGroupItem<CheckboxGroupState> useItemHook={useCheckboxGroupItemAdapter} value={value} id={id} ariaLabel={ariaLabel} stateContext={CheckboxContext} inputVariant={ButtonGroupInputType.Checkbox} contentClassName={itemContentClassName}>
      {children}
    </ButtonGroupItem>;
};